import _ from 'lodash'
import Helper from '../../helpers/Helper'

export type Route = 'overview' | 'playground' | 'contracts' | 'contract-details' | 'provider' | 'provider-group'
    | 'funds' | 'fund' | 'claims' | 'quality-measures-breakdown' | 'provider-performance-breakdown' | 'funds-breakdown'
    | 'claims-breakdown' | 'network' | 'members' | 'contracts-manager' | 'contract-editor' | 'tcoc-summary' | 'provider-contracts'
    | 'member-details' | 'workflows' | 'contract-editor' | 'flows' | 'flow' | 'contracts-analysis' | 'compare-contracts-csi'
    | 'settlement-details' | 'user-profile' | 'sign-in' | 'administrator' | 'user-editor' | 'tooltip-admin' | 'contract-schema' | 'contract-schema-editor'
    | 'user-management' | 'report-editor' | 'media' | 'approvals' | 'users-approvals' | 'groups-management' | 'groups-editor' | 'approval-audit' | 'multirun-result'
    | 'contract-schema-manager' | 'contract-schema-editor'

export interface ISubRoute {
    route: Route
    label: string
    subs?: ISubRoute[]
    parent?: Route
    main?: boolean
    dev?: boolean
    /**
     * Wheter the route should not have padding and be fullscreen.
     */
    fullScreen?: boolean
    /**
     * This field is needed by routes which have 'main':'true' but should not appear on the left Menu
     */
    notShowInMenu?: boolean

}

export interface RouteState {
    route: Route
    subRoute: string
    params?: Record<string, string>
}

export interface IRouteHistory {
    route: Route
    params?: any
    customRouteName?: string
}

export const ROUTES: ISubRoute[] = [
    { route: 'sign-in', label: 'Sign in', fullScreen: true },
    { route: 'overview', label: 'Home', main: true },
    { route: 'contracts', label: 'Contracts', main: true, subs: [
        { route: 'contract-schema', label: 'Contract Schemas', parent: 'contracts', subs: [
            { route: 'contract-schema-editor', label: 'Contract Schemas Editor', parent: 'contract-schema' }
        ] },
        { route: 'contract-schema-manager', label: 'Contract Schemas', parent: 'contracts', subs: [
            { route: 'contract-schema-editor', label: 'Contract Schemas Editor', parent: 'contract-schema-manager' }
        ] },
        { route: 'contracts-manager', label: 'Contract-manager', parent: 'contracts' },
        { route: 'contract-details', label: 'Contract', parent: 'contracts', subs: [
            { route: 'quality-measures-breakdown', label: 'Quality Measures Breakdown', parent: 'contract-details' },
            { route: 'provider-performance-breakdown', label: 'Provider Performance Breakdown', parent: 'contract-details' },
            { route: 'funds-breakdown', label: 'Funds Breakdown', parent: 'contract-details' },
            { route: 'claims-breakdown', label: 'Claims Breakdown', parent: 'contract-details' },
            { route: 'settlement-details', label: 'Settlement Details', parent: 'contract-details' }
        ] },
        { route: 'provider-group', label: 'Provider Group', parent: 'contracts', subs: [
            { route: 'provider', label: 'Provider', parent: 'provider-group' }
        ] },
        { route: 'contracts-manager', label: 'Contract Manager', main: true, parent: 'contract-details', subs: [
            { route: 'contract-editor', label: 'Contract Editor', parent: 'contracts-manager' },
            { route: 'contract-editor', label: 'NEW Contract Editor', parent: 'contracts-manager' }
        ] },
        { route: 'contracts-analysis', label: 'Contracts Analysis', parent: 'contracts', subs: [
            { route: 'compare-contracts-csi', label: 'Contracts CSI', parent: 'contracts-analysis' }
        ] }
    ] },
    { route: 'flows', label: 'Settlement', main: true, subs: [
        { route: 'flow', label: 'Flow', parent: 'flows', fullScreen: true },
        { route: 'multirun-result', label: 'Multi Settlements Result', parent: 'flows' }
    ] },
    { route: 'tcoc-summary', label: 'TCOC Summary', main: true },
    { route: 'members', label: 'Members', main: true, subs: [
        { route: 'member-details', label: 'Member Details', parent: 'members' }
    ] },
    { route: 'network', label: 'Network', main: true, subs: [
        { route: 'provider-contracts', label: 'Provider Contracts', parent: 'network' }
    ] },
    { route: 'workflows', label: 'Workflows', main: true },
    { route: 'user-profile', label: 'User Profile', main: true, notShowInMenu: true },
    { route: 'playground', label: 'Playground', main: true, dev: true },
    { route: 'users-approvals', label: 'Approvals', main: true, subs: [
        { route: 'approval-audit', label: 'Approval Audit', parent: 'approvals' }
    ] },
    { route: 'administrator', label: 'Administrator', main: true, subs: [
        { route: 'tooltip-admin', label: 'Tooltip Manager', parent: 'administrator' },
        { route: 'media', label: 'Media Manager', parent: 'administrator' },
        { route: 'approvals', label: 'Approvals Hub', parent: 'administrator', subs: [
            { route: 'approval-audit', label: 'Approval Audit', parent: 'approvals' }
        ] },
        { route: 'groups-management', label: 'Approval Groups Management', parent: 'administrator', subs: [
            { route: 'groups-editor', label: 'Groups Editor', parent: 'groups-management' }
        ] }
    ] },
    { route: 'report-editor', label: 'Report Editor' }
]

class RoutingManagerClass {
    flat = () => {
        const flatList: ISubRoute[] = []

        const get = (sub: ISubRoute, list: ISubRoute[]) => {
            if (!list.some(x => x.route === sub.route))
                list.push(sub)

            if (sub.subs && sub.subs.length > 0) {
                for (let i = 0; i < sub.subs.length; i++) {
                    get(sub.subs[i], list)
                }
            }
        }

        for (let i = 0; i < ROUTES.length; i++) {
            get(ROUTES[i], flatList)
        }

        return flatList
    }

    getLeftMenuRoutes = () => ROUTES.filter(x => x.main && (!x.dev || x.dev === Helper.isDev()) && x.notShowInMenu !== true)

    get = (route: Route) => this.flat().find(x => x.route === route)

    getParams = <T>(params: any): T => {
        // This can in the future check the url to get any parameters that are passed in the url if not found in the local params
        return (params ?? {}) as T
    }

    changeRoute = (action: IRouteHistory, history: IRouteHistory[]): IRouteHistory[] => {
        const newRoute = this.get(action.route)

        if (newRoute?.main)
            return [action]
        else {
            // Check if it's the same -> ignore
            const current = _.last(history)
            if (current.route === action.route && Helper.deepEquals(current.params, action.params)) return history

            // Check if it's in history -> cut to the past
            const hitIndex = history.findIndex(x => x.route === action.route)
            if (hitIndex > -1) return _.takeRight([...history.slice(0, hitIndex), action], 10)

            // If not -> just add
            return _.takeRight([...history, action], 10)
        }
    }

    getRouteFromLocationStrict(inputString: string): Route | null {
        const segments = inputString
            .split('/')
            .filter(Boolean)

        if (segments.length === 0) return 'overview'

        let current = ROUTES
        let lastValid: Route | null = null

        for (const segment of segments) {
            const match = current.find(r => r.route === segment)
            if (!match) return null

            lastValid = match.route
            current = match.subs ?? []
        }

        return lastValid
    }

    getRouteFromLocation = (): Route => {
        const parts = window.location.pathname
            .split('/')
            .filter(Boolean)

        return (parts[parts.length - 1] ?? 'overview') as Route
    }

    buildPath = (route: Route): string => {
        const segments: string[] = []

        let current = this.get(route)

        while (current) {
            segments.unshift(current.route)
            current = current.parent ? this.get(current.parent) : undefined
        }

        return segments.join('/')
    }
}

const RoutingManager = new RoutingManagerClass()
export default RoutingManager
